import axios from 'axios';
import { getFileExtension, formatFileSize as formatBytes } from '../utils/fileTypes';

const API_URL =
  process.env.REACT_APP_API_URL || 'https://studynest-1t8g.onrender.com/api';

export const SESSION_DURATION_MS = 30 * 60 * 1000; // Exact 30 minutes

const AUTH_STORAGE_KEYS = {
  token: 'token',
  username: 'username',
  sessionExpiresAt: 'admin_session_expires_at',
};

const api = axios.create({
  baseURL: API_URL,
});

function getStorageValue(key) {
  if (typeof window === 'undefined') {
    return '';
  }

  return (
    window.localStorage.getItem(key) ||
    window.sessionStorage.getItem(key) ||
    ''
  );
}

function setStorageValue(key, value) {
  if (typeof window === 'undefined') {
    return;
  }

  window.localStorage.setItem(key, value);
  window.sessionStorage.setItem(key, value);
}

function removeStorageValue(key) {
  if (typeof window === 'undefined') {
    return;
  }

  window.localStorage.removeItem(key);
  window.sessionStorage.removeItem(key);
}

export const decodeJwtPayload = (token) => {
  try {
    const payload = String(token || '').split('.')[1];

    if (!payload) {
      return null;
    }

    const normalized = payload
      .replace(/-/g, '+')
      .replace(/_/g, '/');

    const padded = normalized.padEnd(
      Math.ceil(normalized.length / 4) * 4,
      '='
    );

    return JSON.parse(atob(padded));
  } catch (error) {
    return null;
  }
};

/**
 * Checks whether an active admin session has expired (30m limit).
 * Returns true ONLY if a token exists and has passed the 30-minute mark.
 */
export const isSessionExpired = () => {
  const token = getStorageValue(AUTH_STORAGE_KEYS.token);
  if (!token) return false; // Guest / student mode (not expired)

  const expiresAtRaw = getStorageValue(AUTH_STORAGE_KEYS.sessionExpiresAt);
  const expiresAt = Number(expiresAtRaw);

  // 1. Check persisted 30-minute timestamp
  if (expiresAt && Date.now() >= expiresAt) {
    return true;
  }

  // 2. Cross-verify with JWT exp payload
  const decoded = decodeJwtPayload(token);
  if (decoded && decoded.exp && Date.now() >= decoded.exp * 1000) {
    return true;
  }

  return false;
};

export const readStoredAuth = () => {
  const token = getStorageValue(AUTH_STORAGE_KEYS.token);
  if (!token) {
    return { token: '', username: '' };
  }

  if (isSessionExpired()) {
    clearStoredAuth();
    return { token: '', username: '' };
  }

  return {
    token,
    username: getStorageValue(AUTH_STORAGE_KEYS.username),
  };
};

/**
 * Starts a new fixed 30-minute session upon login.
 */
export const persistStoredAuth = ({ token, username, expiresAt }) => {
  const finalExpiresAt = expiresAt || String(Date.now() + SESSION_DURATION_MS);

  setStorageValue(AUTH_STORAGE_KEYS.token, token || '');
  setStorageValue(AUTH_STORAGE_KEYS.username, username || '');
  setStorageValue(AUTH_STORAGE_KEYS.sessionExpiresAt, finalExpiresAt);
};

export const clearStoredAuth = () => {
  removeStorageValue(AUTH_STORAGE_KEYS.token);
  removeStorageValue(AUTH_STORAGE_KEYS.username);
  removeStorageValue(AUTH_STORAGE_KEYS.sessionExpiresAt);
};

export const getAuthHeaders = (token) => {
  const t = token || readStoredAuth().token;
  return t ? { Authorization: `Bearer ${t}` } : {};
};

// Automatic Axios Request Interceptor: injects Bearer token for admin requests
api.interceptors.request.use(
  (config) => {
    const token = getStorageValue(AUTH_STORAGE_KEYS.token);

    if (token) {
      if (isSessionExpired()) {
        clearStoredAuth();
        if (typeof window !== 'undefined' && window.location.pathname.startsWith('/admin')) {
          window.location.href = '/login';
        }
        return Promise.reject(new axios.Cancel('Admin session expired (30m limit reached).'));
      }
      if (!config.headers.Authorization) {
        config.headers.Authorization = `Bearer ${token}`;
      }
    }

    return config;
  },
  (error) => Promise.reject(error)
);

// Automatic Axios Response Interceptor: handles 401/403 token expiration
api.interceptors.response.use(
  (response) => response,
  (error) => {
    if (error.response && (error.response.status === 401 || error.response.status === 403)) {
      if (typeof window !== 'undefined' && window.location.pathname.startsWith('/admin')) {
        clearStoredAuth();
        window.location.href = '/login';
      }
    }
    return Promise.reject(error);
  }
);

export const getDownloadUrl = (documentId) =>
  `${API_URL}/documents/${documentId}/download`;

export const getViewUrl = (documentId) =>
  `${API_URL}/documents/${documentId}/view`;

export const getFileFormat = (doc) => {
  return getFileExtension(doc);
};

export const getFilePreviewUrl = (doc) => {
  if (!doc) return '#';
  const format = getFileFormat(doc);
  if (['doc', 'docx', 'ppt', 'pptx', 'xls', 'xlsx'].includes(format)) {
    return `https://docs.google.com/viewer?url=${encodeURIComponent(doc.cloudinaryUrl || getViewUrl(doc._id))}&embedded=true`;
  }
  return doc._id ? getViewUrl(doc._id) : (doc.cloudinaryUrl || '#');
};

export const formatFileSize = (bytes) => {
  return formatBytes(bytes);
};

export const isRequestCancelled = (error) => {
  return (
    axios.isCancel(error) ||
    error?.code === 'ERR_CANCELED' ||
    error?.name === 'CanceledError'
  );
};

export default api;